import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { goauthHttp } from './goauth-http'

/**
 * The provider's public self-description. Unauthenticated, cached by goauth for
 * a minute, and the only way to learn whether registration is open — the route
 * is mounted at boot, so a closed one is indistinguishable from a missing one.
 */
const settingsSchema = z.object({
  registration: z.enum(['open', 'closed']),
  min_password_length: z.number(),
  issuer: z.string(),
})

export type AuthSettings = {
  isRegistrationOpen: boolean
  minPasswordLength: number
}

/**
 * The floor is the provider's to set, so it is read rather than hardcoded — a
 * form that guesses it either rejects valid passwords or lets the server do the
 * rejecting. This value is only a courtesy check; goauth stays the authority.
 */
const FALLBACK_MIN_PASSWORD_LENGTH = 12

export async function fetchAuthSettings(signal?: AbortSignal): Promise<AuthSettings> {
  const raw = await goauthHttp.get('auth/settings', { schema: settingsSchema, signal })

  return {
    isRegistrationOpen: raw.registration === 'open',
    minPasswordLength: raw.min_password_length,
  }
}

export const authSettingsQuery = () =>
  queryOptions({
    queryKey: ['auth-settings'],
    queryFn: ({ signal }) => fetchAuthSettings(signal),
    // Provider configuration, not user data: it changes on a restart, never
    // mid-session, and a failed read must not hide the sign-in form.
    staleTime: Infinity,
    retry: false,
  })

/** Used while the query is still in flight, or when the provider is unreachable. */
export const minPasswordLengthOf = (settings: AuthSettings | undefined) =>
  settings?.minPasswordLength ?? FALLBACK_MIN_PASSWORD_LENGTH
